import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CollapseHeader from "../../core/common/collapse-header";
import DealReportModal from "../../core/modals/dealReportModal";
import { fetchAllDeals, Deal } from "../../api/deal";
import { ApexOptions } from "apexcharts";
import Select from "react-select";
import PageLoadProgressBar from "../../core/data/json/pageLoadProgressBar";
import Chart from "react-apexcharts";
import KpiCard from "../../core/common/KPIcard";
import { useDebounce } from "../../api/usedebounce";
import Tip from "../../core/common/tooltip"
import ChartExportControl from "../../core/common/ChartExportControl";
import { components } from "react-select";
import ScrollToTop from "../../core/common/ScrollToTop";

const TimeFrameValueContainer = (props: any) => {
  return (
    <components.ValueContainer {...props}>
      <i
        className="ti ti-calendar-stats me-2 text-muted"
        style={{ fontSize: "16px" }}
      />
      {props.children}
    </components.ValueContainer>
  );
};

const DealReport = () => {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timeFrame, setTimeFrame] = useState("Monthly");
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 400);

  useEffect(() => {
    const fetchDealsData = async () => {
      setIsLoading(true);
      try {
        const data = await fetchAllDeals();
        setDeals(data || []);
      } catch (error) {
        console.error("Error fetching deals:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDealsData();
  }, []);

  useEffect(() => {
    const makeModalDraggable = (modalId: string) => {
      const modalElement = document.getElementById(modalId);
      if (!modalElement) return;

      const modalDialog = modalElement.querySelector(
        ".modal-dialog"
      ) as HTMLElement;
      const modalHeader = modalElement.querySelector(
        ".modal-header"
      ) as HTMLElement;

      if (!modalDialog || !modalHeader) return;

      let isDragging = false;
      let currentX = 0;
      let currentY = 0;
      let initialX = 0;
      let initialY = 0;
      let startX = 0;
      let startY = 0;

      // Reset position when modal is shown
      const resetPosition = () => {
        // Reset all position variables
        currentX = 0;
        currentY = 0;
        initialX = 0;
        initialY = 0;
        startX = 0;
        startY = 0;
        isDragging = false;

        // Remove only the inline styles that were added during dragging
        modalDialog.style.position = "";
        modalDialog.style.top = "";
        modalDialog.style.left = "";
        modalDialog.style.transform = "";
        modalDialog.style.margin = "";
        modalDialog.style.transition = "";
        modalDialog.style.width = "";
        modalDialog.style.maxWidth = "";

        // Restore centered class if it was removed
        if (!modalDialog.classList.contains("modal-dialog-centered")) {
          modalDialog.classList.add("modal-dialog-centered");
        }
      };

      // Listen for modal show event - reset when modal is about to show
      const handleShow = () => {
        // Reset immediately when modal starts showing
        resetPosition();
      };

      // Listen for modal shown event - ensure reset after modal is fully displayed
      const handleShown = () => {
        // Double-check reset after modal is fully shown
        resetPosition();
      };

      // Listen for modal hidden event - reset when modal is fully hidden
      const handleHidden = () => {
        resetPosition();
      };

      modalElement.addEventListener("show.bs.modal", handleShow);
      modalElement.addEventListener("shown.bs.modal", handleShown);
      modalElement.addEventListener("hidden.bs.modal", handleHidden);

      const dragStart = (e: MouseEvent) => {
        // Only allow dragging from header, not from close button or other interactive elements
        const target = e.target as HTMLElement;
        if (
          target.closest(".btn-close") ||
          target.closest("button") ||
          target.closest("input") ||
          target.closest("select")
        ) {
          return;
        }

        if (
          e.target === modalHeader ||
          modalHeader.contains(e.target as Node)
        ) {
          isDragging = true;
          modalHeader.style.cursor = "move";

          // Get current position and size of modal dialog
          const rect = modalDialog.getBoundingClientRect();
          startX = rect.left;
          startY = rect.top;

          // Get computed styles to preserve the modal's size
          const computedStyle = getComputedStyle(modalDialog);
          const currentWidth = computedStyle.width;
          const currentMaxWidth = computedStyle.maxWidth;
          const currentMinWidth = computedStyle.minWidth;

          // Remove centered class to allow free movement
          modalDialog.classList.remove("modal-dialog-centered");

          // Set initial position based on current position
          modalDialog.style.position = "fixed";
          modalDialog.style.top = `${startY}px`;
          modalDialog.style.left = `${startX}px`;
          modalDialog.style.margin = "0";
          modalDialog.style.transform = "none";
          modalDialog.style.transition = "none";

          // Preserve the modal's width and max-width to prevent size change
          if (
            currentWidth &&
            currentWidth !== "auto" &&
            currentWidth !== "0px"
          ) {
            modalDialog.style.width = currentWidth;
          }
          if (
            currentMaxWidth &&
            currentMaxWidth !== "none" &&
            currentMaxWidth !== "0px"
          ) {
            modalDialog.style.maxWidth = currentMaxWidth;
          }
          if (
            currentMinWidth &&
            currentMinWidth !== "0px" &&
            currentMinWidth !== "auto"
          ) {
            modalDialog.style.minWidth = currentMinWidth;
          }

          initialX = e.clientX - startX;
          initialY = e.clientY - startY;
        }
      };

      const drag = (e: MouseEvent) => {
        if (!isDragging) return;

        e.preventDefault();

        // Calculate new position
        const newX = e.clientX - initialX;
        const newY = e.clientY - initialY;

        // Get modal dialog dimensions
        const rect = modalDialog.getBoundingClientRect();
        const maxX = window.innerWidth - rect.width;
        const maxY = window.innerHeight - rect.height;

        // Constrain to viewport
        const constrainedX = Math.max(0, Math.min(newX, maxX));
        const constrainedY = Math.max(0, Math.min(newY, maxY));

        // Update position
        modalDialog.style.left = `${constrainedX}px`;
        modalDialog.style.top = `${constrainedY}px`;

        // Store current position for next drag
        currentX = constrainedX;
        currentY = constrainedY;
      };

      const dragEnd = () => {
        if (isDragging) {
          isDragging = false;
          modalHeader.style.cursor = "move";
          modalDialog.style.transition = "";
          // Keep the fixed positioning during drag, but allow reset on next open
        }
      };

      // Add cursor style to header
      modalHeader.style.cursor = "move";
      modalHeader.style.userSelect = "none";

      // Event listeners
      modalHeader.addEventListener("mousedown", dragStart);
      document.addEventListener("mousemove", drag);
      document.addEventListener("mouseup", dragEnd);

      // Cleanup function
      return () => {
        modalElement.removeEventListener("show.bs.modal", handleShow);
        modalElement.removeEventListener("shown.bs.modal", handleShown);
        modalElement.removeEventListener("hidden.bs.modal", handleHidden);
        modalHeader.removeEventListener("mousedown", dragStart);
        document.removeEventListener("mousemove", drag);
        document.removeEventListener("mouseup", dragEnd);
      };
    };

    // Make all three modals draggable
    const cleanup1 = makeModalDraggable("add_calls");
    const cleanup2 = makeModalDraggable("edit_calls");
    const cleanup3 = makeModalDraggable("delete_calls");
    const cleanup4 = makeModalDraggable("bulk_delete_modal");
    const cleanup5 = makeModalDraggable("import_excel_modal");
    const cleanup6 = makeModalDraggable("download_deal_report");

    return () => {
      cleanup1?.();
      cleanup2?.();
      cleanup3?.();
      cleanup4?.();
      cleanup5?.();
      cleanup6?.();
    };
  }, []);

  useEffect(() => {
    if (deals.length > 0) {
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 300);
    }
  }, [deals]);

  const filteredDeals = useMemo(() => {
    const term = debouncedSearch.toLowerCase().trim();
    if (!term) return deals;

    return deals.filter(
      (deal) =>
        deal.Description?.toLowerCase().includes(term) ||
        deal.dealsource?.deal_source_nema?.toLowerCase().includes(term) ||
        deal.contact?.contact_person_name?.toLowerCase().includes(term)
    );
  }, [debouncedSearch, deals]);

  // ================= Deals by Source (Donut) =================
  const getDealsBySource = useMemo(() => {
    const sourceMap: Record<string, number> = {};

    filteredDeals.forEach((deal) => {
      const source = deal.dealsource?.deal_source_nema || "Unknown";
      sourceMap[source] = (sourceMap[source] || 0) + 1;
    });

    const labels = Object.keys(sourceMap);
    const series = Object.values(sourceMap);

    return { labels, series };
  }, [filteredDeals]);

  const totalSourceDeals = useMemo(
    () => getDealsBySource.series.reduce((a, b) => a + b, 0),
    [getDealsBySource.series]
  );

  const donutOptions = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: "donut",
        animations: { enabled: true, speed: 600 },
      },
      labels: getDealsBySource.labels,
      colors: [
        "#0092E4",
        "#4A00E5",
        "#00B894",
        "#FFA201",
        "#E41F07",
        "#5A5AFE",
      ],
      plotOptions: {
        pie: {
          startAngle: -90,
          endAngle: 270,
          donut: {
            size: "70%",
            labels: {
              show: true,
              name: { show: true, fontSize: "14px" },
              value: {
                show: true,
                fontSize: "22px",
                fontWeight: 600,
                formatter: (val) => `${val}`,
              },
              total: {
                show: true,
                label: "Total Deals",
                fontSize: "13px",
                formatter: () => `${totalSourceDeals}`,
              },
            },
          },
        },
      },
      dataLabels: { enabled: false },
      tooltip: {
        y: {
          formatter: (value: number) => {
            const percent = ((value / totalSourceDeals) * 100).toFixed(1);
            return `${value} deals (${percent}%)`;
          },
        },
      },
      legend: {
        position: "bottom",
        fontSize: "13px",
        formatter: (val, opts) => {
          const count = getDealsBySource.series[opts.seriesIndex];
          const percent = ((count / totalSourceDeals) * 100).toFixed(1);
          return `${val} — ${count} (${percent}%)`;
        },
      },
      responsive: [{ breakpoint: 768, options: { chart: { height: 280 } } }],
    }),
    [getDealsBySource, totalSourceDeals]
  );

  // ================= Deals by Month (Stacked Bar) =================
  const getDealsByMonth = useMemo(() => {
    const map: Record<
      string,
      { open: number; lost: number; won: number; Closed: number }
    > = {};

    filteredDeals.forEach((deal) => {
      const created = new Date(deal.created_at);
      let key = "";

      if (timeFrame === "Monthly") {
        key = created.toLocaleString("default", {
          month: "short",
          year: "numeric",
        });
      } else if (timeFrame === "Quarterly") {
        const q = Math.floor(created.getMonth() / 3) + 1;
        key = `Q${q} ${created.getFullYear()}`;
      } else {
        key = created.getFullYear().toString();
      }

      if (!map[key]) {
        map[key] = { open: 0, lost: 0, won: 0, Closed: 0 };
      }

      const status = (deal.status || "open").toLowerCase();

      if (status === "won") map[key].won++;
      else if (status === "lost") map[key].lost++;
      else if (status === "closed") map[key].Closed++;
      else map[key].open++;
    });

    const sortedKeys = Object.keys(map).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );

    return sortedKeys.map((key) => ({
      x: key,
      open: map[key].open,
      lost: map[key].lost,
      won: map[key].won,
      Closed: map[key].Closed,
    }));
  }, [filteredDeals]);

  const barOptions = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: "bar",
        height: 275,
        toolbar: { show: false },
        stacked: true,
        animations: { enabled: true, speed: 600 },
      },
      plotOptions: {
        bar: { horizontal: false, borderRadius: 6, columnWidth: "60%" },
      },
      colors: ["#00918E", "#E41F07", "#00B894", "#FFA201"], // Open, Lost, Won, Close
      dataLabels: { enabled: false },
      grid: { strokeDashArray: 4, borderColor: "#e9ecef" },
      xaxis: {
        type: "category",
        categories: getDealsByMonth.map((m) => m.x),
        title: {
          text: timeFrame,
          style: { fontSize: "12px", fontWeight: 500 },
        },
      },
      yaxis: {
        title: {
          text: "Number of Deals",
          style: { fontSize: "12px", fontWeight: 500 },
        },
      },
      tooltip: { shared: true, intersect: false },
      legend: { position: "bottom" },
      responsive: [{ breakpoint: 768, options: { chart: { height: 240 } } }],
    }),
    [getDealsByMonth, timeFrame]
  );

  // ================= Deal Status Funnel (Using Your Exact Status List) =================
  const dealStatusOrder = useMemo(
    () => [
      { value: "open", label: "Open" },
      { value: "Closed", label: "Closed" },
      { value: "won", label: "Won" },
      { value: "lost", label: "Lost" },
    ],
    []
  );

  const getFunnelData = useMemo(() => {
    const statusMap: Record<string, number> = {};

    filteredDeals.forEach((deal) => {
      const status = deal.status || "open";
      statusMap[status] = (statusMap[status] || 0) + 1;
    });

    return dealStatusOrder.map((item) => ({
      x: item.label,
      y: statusMap[item.value] || 0,
    }));
  }, [filteredDeals, dealStatusOrder]);

  const funnelOptions = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: "bar",
        height: 350,
        animations: { enabled: true, speed: 600 },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 8,
          barHeight: "70%",
          distributed: true,
          dataLabels: { position: "center" },
        },
      },
      colors: ["#00918E", "#FFA201", "#00B894", "#E41F07"], // Open, Close, Won, Lost
      dataLabels: {
        enabled: true,
        formatter: (val: number) => `${val} deals`,
        style: { fontSize: "14px", fontWeight: 600, colors: ["#fff"] },
      },
      xaxis: { type: "category", labels: { show: false } },
      yaxis: {
        title: {
          text: "Deal Stages",
          style: { fontSize: "12px", fontWeight: 500 },
        },
      },
      grid: { show: false },
      tooltip: { y: { formatter: (val: number) => `${val} deals` } },
      title: {
        text: "Deal Status Funnel",
        align: "center",
        style: { fontSize: "15px", fontWeight: 600 },
      },
      legend: { show: false },
      responsive: [{ breakpoint: 768, options: { chart: { height: 300 } } }],
    }),
    []
  );

  // ================= Deal Activity Heatmap =================
  const getHeatmapData = useMemo(() => {
    const monthMap: Record<string, number[]> = {};

    filteredDeals.forEach((deal) => {
      const date = new Date(deal.created_at);
      const monthKey = date.toLocaleString("default", {
        month: "short",
        year: "numeric",
      });
      const dayIndex = date.getDay();

      if (!monthMap[monthKey]) monthMap[monthKey] = [0, 0, 0, 0, 0, 0, 0];
      monthMap[monthKey][dayIndex]++;
    });

    const sortedMonths = Object.keys(monthMap).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );

    return sortedMonths.map((month) => ({
      name: month,
      data: monthMap[month],
    }));
  }, [filteredDeals]);

  const heatmapOptions = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: "heatmap",
        height: 450,
        toolbar: { show: false },
        animations: { enabled: true },
      },
      dataLabels: { enabled: false },
      colors: ["#008FFB"],
      plotOptions: {
        heatmap: {
          shadeIntensity: 0.8,
          radius: 8,
          useFillColorAsStroke: true,
          colorScale: {
            ranges: [
              { from: 0, to: 0, color: "#f8f9fa" },
              { from: 1, to: 5, color: "#E0F7FA" },
              { from: 6, to: 15, color: "#80DEEA" },
              { from: 16, to: 30, color: "#00ACC1" },
              { from: 31, to: 999, color: "#006064" },
            ],
          },
        },
      },
      xaxis: {
        categories: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
        title: {
          text: "Day of Week",
          style: { fontSize: "12px", fontWeight: 500 },
        },
      },
      yaxis: {
        title: { text: "Month", style: { fontSize: "12px", fontWeight: 500 } },
      },
      title: {
        text: "Deal Activity Heatmap (by Day of Week)",
        align: "center",
        style: { fontSize: "15px", fontWeight: 600 },
      },
      tooltip: {
        y: {
          formatter: (val: number) => (val === 0 ? "No deals" : `${val} deals`),
        },
      },
      legend: { position: "bottom" },
    }),
    []
  );

  // ================= KPI Calculations =================
  const now = new Date();
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const totalDeals = filteredDeals.length;

  const dealsThisMonth = useMemo(
    () =>
      filteredDeals.filter(
        (deal) => new Date(deal.created_at) >= firstDayOfMonth
      ).length,
    [filteredDeals]
  );



  const topDealSource = useMemo(() => {
    if (filteredDeals.length === 0) return { name: "-", count: 0 };

    const sourceMap: Record<string, number> = {};
    filteredDeals.forEach((deal) => {
      const source = deal.dealsource?.deal_source_nema || "Unknown";
      sourceMap[source] = (sourceMap[source] || 0) + 1;
    });

    const [name, count] = Object.entries(sourceMap).sort(
      (a, b) => b[1] - a[1]
    )[0] || ["-", 0];
    return { name, count };
  }, [filteredDeals]);

  const avgDealsPerMonth = useMemo(() => {
    if (filteredDeals.length === 0) return 0;

    const dates = filteredDeals.map((d) => new Date(d.created_at));
    const minDate = new Date(Math.min(...dates.map((d) => d.getTime())));
    const monthDiff =
      (now.getFullYear() - minDate.getFullYear()) * 12 +
      (now.getMonth() - minDate.getMonth()) +
      1;

    return Math.round(totalDeals / monthDiff);
  }, [filteredDeals, totalDeals]);

  const timeFrameSelectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      minHeight: 40,
      borderRadius: 8,
      borderColor: state.isFocused ? "#0d6efd" : "#dee2e6",
      boxShadow: state.isFocused ? "0 0 0 1px #0d6efd" : "none",
      "&:hover": {
        borderColor: "#0d6efd",
      },
      cursor: "pointer",
    }),
    option: (base: any, state: any) => ({
      ...base,
      padding: "8px 12px",
      backgroundColor: state.isSelected
        ? "#0d6efd"
        : state.isFocused
          ? "#f1f5ff"
          : "#fff",
      color: state.isSelected ? "#fff" : "#212529",
      cursor: "pointer",
    }),
    singleValue: (base: any) => ({
      ...base,
      fontWeight: 500,
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: "#6c757d",
      "&:hover": { color: "#0d6efd" },
    }),
    valueContainer: (base: any) => ({
      ...base,
      display: "flex",
      alignItems: "center",
      paddingLeft: 12,
    }),
  };

  const totalDealsPercent = "100";

  const dealsThisMonthPercent =
    totalDeals === 0
      ? "0"
      : ((dealsThisMonth / totalDeals) * 100).toFixed(1);

  const topDealSourcePercent =
    totalDeals === 0
      ? "0"
      : ((topDealSource.count / totalDeals) * 100).toFixed(1);

  const avgDealsPercent =
    totalDeals === 0
      ? "0"
      : ((avgDealsPerMonth / totalDeals) * 100).toFixed(1);

  return (
    <>
      <PageLoadProgressBar />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header mb-4">
            <div className="row align-items-center">
              <div className="col-sm-8">
                <h4 className="page-title d-flex align-items-center gap-2">
                  <i className="ti ti-file-invoice text-primary" />
                  Deals Report
                  <Tip
                    text="Comprehensive overview of deal performance, pipeline health, sources, and trends."
                    placement="right"
                  >
                    <i
                      className="fa fa-info-circle text-primary"
                      style={{ cursor: "pointer", fontSize: "15px" }}
                    />
                  </Tip>
                </h4>
                <small className="text-muted">
                  Total: {filteredDeals.length} Deals
                </small>
              </div>
              <div className="col-sm-4 text-sm-end">
                <div className="head-icons">
                  <CollapseHeader />
                </div>
              </div>
            </div>
          </div>

        
<div className="row g-3 mb-4">
  {(() => {
    const total = totalDeals;

    const dealsThisMonthPercentCalc =
      total > 0 ? ((dealsThisMonth / total) * 100).toFixed(1) : "0";

    const topDealSourcePercentCalc =
      total > 0 ? ((topDealSource.count / total) * 100).toFixed(1) : "0";

    const avgDealsPercentCalc =
      total > 0 ? ((avgDealsPerMonth / total) * 100).toFixed(1) : "0";

    return (
      <>
        <div className="row g-3">
          {/* TOTAL DEALS */}
          <div className="col-12 col-sm-6 col-lg-3">
            <Tip
              text={`Total Deals: ${totalDeals} (100%)`}
              placement="top"
            >
              <div className="kpi-card-wrapper h-100">
                <KpiCard
                  title="Deals"
                  subtitle="Total"
                  value={totalDeals}
                  icon="ti ti-user-check"
                  color="primary"
                  tooltip="Total number of deals in the system"
                />
              </div>
            </Tip>
          </div>

          {/* DEALS THIS MONTH */}
          <div className="col-12 col-sm-6 col-lg-3">
            <Tip
              text={`Deals This Month: ${dealsThisMonth} (${dealsThisMonthPercentCalc}%)`}
              placement="top"
            >
              <div className="kpi-card-wrapper h-100">
                <KpiCard
                  title="Deals"
                  subtitle="This Month"
                  value={dealsThisMonth}
                  icon="ti ti-trending-up"
                  color="success"
                  tooltip="Deals created in the current month"
                  percentage={dealsThisMonthPercentCalc}
                  percentageColor={
                    parseFloat(dealsThisMonthPercentCalc) > 0
                      ? "success"
                      : "danger"
                  }
                  percentageIcon={
                    parseFloat(dealsThisMonthPercentCalc) > 0
                      ? "ti ti-arrow-up"
                      : "ti ti-arrow-down"
                  }
                />
              </div>
            </Tip>
          </div>

          {/* TOP DEAL SOURCE */}
          <div className="col-12 col-sm-6 col-lg-3">
            <Tip
              text={`Top Deal Source: ${topDealSource.name} (${topDealSourcePercentCalc}%)`}
              placement="top"
            >
              <div className="kpi-card-wrapper h-100">
                <KpiCard
                  title="Deal Source"
                  subtitle={`Top: ${topDealSource.name}`}
                  value={topDealSource.count}
                  icon="ti ti-broadcast"
                  color="warning"
                  tooltip="Deal source generating the most deals"
                  percentage={topDealSourcePercentCalc}
                  percentageColor="success"
                  percentageIcon="ti ti-arrow-up"
                />
              </div>
            </Tip>
          </div>

          {/* AVG DEALS / MONTH */}
          <div className="col-12 col-sm-6 col-lg-3">
            <Tip
              text={`Avg Deals / Month: ${avgDealsPerMonth} (${avgDealsPercentCalc}%)`}
              placement="top"
            >
              <div className="kpi-card-wrapper h-100">
                <KpiCard
                  title="Deals"
                  subtitle="Avg / Month"
                  value={avgDealsPerMonth}
                  icon="ti ti-chart-line"
                  color="info"
                  tooltip="Average deals generated per month"
                  percentage={avgDealsPercentCalc}
                  percentageColor="info"
                  percentageIcon="ti ti-arrow-up"
                />
              </div>
            </Tip>
          </div>
        </div>
      </>
    );
  })()}
</div>

          <div className="card shadow-sm mb-4">
            <div className="card-header bg-light d-flex justify-content-between align-items-center">
              <div className="input-group w-50">
                <span className="input-group-text bg-white border-end-0">
                  <i className="fa fa-search text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Search by source, contact, description… (e.g. Facebook, John)"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                {searchInput && (
                  <button
                    className="btn btn-light border"
                    onClick={() => setSearchInput("")}
                    title="Clear"
                  >
                    <i className="ti ti-x" />
                  </button>
                )}
              </div>

              <Select
                className="w-25"
                options={[
                  { value: "", label: "Choose" },
                  { value: "Monthly", label: "Monthly" },
                  { value: "Quarterly", label: "Quarterly" },
                  { value: "Yearly", label: "Yearly" },
                ]}
                defaultValue={{ value: "Monthly", label: "Monthly" }}
                isSearchable={false}
                styles={timeFrameSelectStyles}
                components={{
                  ValueContainer: TimeFrameValueContainer,
                }}
                onChange={(e) => setTimeFrame(e?.value || "Monthly")}
              />

              <Link
                to="#"
                className="btn btn-primary"
                data-bs-toggle="modal"
                data-bs-target="#download_deal_report"
                style={{
                                position: "relative",
                                overflow: "hidden",
                              }}
              >
                <i className="ti ti-file-download me-2" /> Download Report
                <span
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  width: "100%",
                                  height: "100%",
                                  background: "linear-gradient(to right, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%)",
                                  transform: "skewX(-20deg) translateX(-150%)",
                                  animation: "shine 3s infinite",
                                }}
                              />
              </Link>
            </div>

            {debouncedSearch && filteredDeals.length === 0 && (
              <div className="text-center py-5 text-muted">
                <i className="ti ti-database-off fs-2 mb-2" />
                <p>No deals found for "{debouncedSearch}"</p>
              </div>
            )}

            <div className="card-body">
              <div className="row g-4">
                {/* Deals by TimeFrame (Stacked Bar) */}
                <div className="col-lg-6">
                  <div className="card h-100 shadow-sm" id="deals-by-timeframe">
                    <div className="card-header">
                      <div className="d-flex align-items-center justify-content-between flex-nowrap">
                        <div>
                          <h5 className="mb-0 d-flex align-items-center gap-2">
                            <i className="ti ti-chart-bar text-primary" />
                            Deals by {timeFrame}
                            <Tip text="Stacked view of deal statuses over time.">
                              <i
                                className="fa fa-info-circle text-primary"
                                style={{ cursor: "pointer", fontSize: "14px" }}
                              />
                            </Tip>
                          </h5>
                          <small className="text-muted">
                            Showing {getDealsByMonth.length} periods
                          </small>
                        </div>
                        <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                          <ChartExportControl
                            data={getDealsByMonth}
                            filename={`Deals_by_${timeFrame}`}
                            cardId="deals-by-timeframe"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="card-body">
                      {getDealsByMonth.length === 0 ? (
                        <div className="text-center text-muted py-5">
                          <i className="ti ti-chart-bar-off fs-2 mb-2" />
                          <p>No data available</p>
                        </div>
                      ) : (
                        <Chart
                          options={barOptions}
                          series={[
                            {
                              name: "Open",
                              data: getDealsByMonth.map((m) => m.open),
                            },
                            {
                              name: "Closed",
                              data: getDealsByMonth.map((m) => m.Closed),
                            },
                            {
                              name: "Won",
                              data: getDealsByMonth.map((m) => m.won),
                            },
                            {
                              name: "Lost",
                              data: getDealsByMonth.map((m) => m.lost),
                            },
                          ]}
                          type="bar"
                          height={275}
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Deals by Source (Donut) */}
                <div className="col-lg-6">
                  <div className="card h-100 shadow-sm" id="deals-by-source">
                    <div className="card-header">
                      <div className="d-flex align-items-center justify-content-between flex-nowrap">
                        <div>
                          <h5 className="mb-0 d-flex align-items-center gap-2">
                            <i className="ti ti-chart-donut text-primary" />
                            Deals by Source
                            <Tip text="Distribution of deals by origin source.">
                              <i
                                className="fa fa-info-circle text-primary"
                                style={{ cursor: "pointer", fontSize: "14px" }}
                              />
                            </Tip>
                          </h5>
                          <small className="text-muted">
                            {getDealsBySource.labels.length} sources
                          </small>
                        </div>
                        <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                          <ChartExportControl
                            data={getDealsBySource.series.map((val, i) => ({
                              Source: getDealsBySource.labels[i],
                              Count: val,
                            }))}
                            filename="Deals_by_Source"
                            cardId="deals-by-source"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="card-body d-flex align-items-center justify-content-center">
                      {getDealsBySource.series.length === 0 ? (
                        <div className="text-center text-muted py-5">
                          <i className="ti ti-chart-pie-off fs-2 mb-2" />
                          <p>No source data</p>
                        </div>
                      ) : (
                        <div style={{ width: "100%", height: "350px" }}>
                          <Chart
                            options={donutOptions}
                            series={getDealsBySource.series}
                            type="donut"
                            height={350}
                            width="100%"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Deal Status Funnel */}
                <div className="col-lg-6">
                  <div className="card h-100 shadow-sm" id="deal-status-funnel">
                    <div className="card-header">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <h5 className="mb-0 d-flex align-items-center gap-2">
                            <i className="ti ti-activity text-primary" />
                            Deal Status Funnel
                            <Tip text="Current pipeline health and stage distribution.">
                              <i
                                className="fa fa-info-circle text-primary"
                                style={{ cursor: "pointer", fontSize: "14px" }}
                              />
                            </Tip>
                          </h5>
                          <small className="text-muted">
                            Pipeline snapshot
                          </small>
                        </div>
                        <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                          <ChartExportControl
                            data={getFunnelData}
                            filename="Deal_Status_Funnel"
                            cardId="deal-status-funnel"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="card-body">
                      {getFunnelData.every((d) => d.y === 0) ? (
                        <div className="text-center text-muted py-5">
                          <i className="ti ti-filter-off fs-2 mb-2" />
                          <p>No stage data</p>
                        </div>
                      ) : (
                        <Chart
                          options={funnelOptions}
                          series={[{ data: getFunnelData }]}
                          type="bar"
                          height={350}
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Heatmap */}
                <div className="col-lg-6">
                  <div className="card h-100 shadow-sm" id="deal-activity-heatmap">
                    <div className="card-header">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <h5 className="mb-0 d-flex align-items-center gap-2">
                            <i className="ti ti-calendar-stats text-primary" />
                            Deal Activity Heatmap
                            <Tip text="Patterns in deal creation by day of week across months.">
                              <i
                                className="fa fa-info-circle text-primary"
                                style={{ cursor: "pointer", fontSize: "14px" }}
                              />
                            </Tip>
                          </h5>
                          <small className="text-muted">
                            {getHeatmapData.length} months
                          </small>
                        </div>
                        <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                          <ChartExportControl
                            data={getHeatmapData.map((m) => ({
                              Month: m.name,
                              Sun: m.data[0],
                              Mon: m.data[1],
                              Tue: m.data[2],
                              Wed: m.data[3],
                              Thu: m.data[4],
                              Fri: m.data[5],
                              Sat: m.data[6],
                            }))}
                            filename="Deal_Activity_Heatmap"
                            cardId="deal-activity-heatmap"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="card-body">
                      {getHeatmapData.length === 0 ? (
                        <div className="text-center text-muted py-5">
                          <i className="ti ti-calendar-off fs-2 mb-2" />
                          <p>No activity data</p>
                        </div>
                      ) : (
                        <Chart
                          options={heatmapOptions}
                          series={getHeatmapData}
                          type="heatmap"
                          height={450}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DealReportModal />
        </div>
      </div>
      <ScrollToTop />
    </>
  );
};

export default DealReport;
